import { ChangeDetectionStrategy, Component, Input, Predicate, booleanAttribute, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { HisOptionGroup, HisOptionXtd } from '../../../../core/model/his-option';
import { HisBackendService } from '../../../../core/service/backend/his-backend.service';
import { HisUiService } from '../../../../core/service/base/his-ui.service';
import { HisNotificationService } from '../../../../core/service/notifications/his-notification.service';

interface DataSourceResult {
    source: string;
    options: HisOptionXtd[];
}

interface QueryPredicate extends Predicate<HisOptionXtd> {
    query: string;
}

/**
 * A multiselect component with autocomplete functionality. It allows users to select multiple options from a list, with the ability to filter the options based on user input.
 * 
 * The component operates in 3 modes:
 * 1. **Eager mode:** The `optionList` input is provided with all available options. The component filters these options based on user input.
 * 2. **Lazy mode:** The `optionList` input is _not_ provided. The component emits a `lazyLoadRequest` event when the user starts typing,
 *    allowing the parent component to fetch the options asynchronously.
 * 3. **DataSource mode:** The `dataSource` input is provided with a URL or class name. The component fetches the options from the backend using this data source.
 * 
 * Priority order:\
 * If a `dataSource` is provided, it takes priority over the `optionList`. If neither is provided, the component will emit a `lazyLoadRequest` event to fetch options on demand.
 * 
 * The loaded suggestions first are filtered using the `query` value, then filtered again to remove already selected options (if `removeSelectedOptionsFromDropdown` is true),
 * and finally limited to the number specified by the `limit` input.
 * 
 * Further UX feature:
 * - While the suggestions are still loading, a spinner lets the user know that options are on their way.
 * - If the suggestions are limited, a hint is displayed above the options to indicate how many options are shown out of the total available. Additionally, a button is provided
 *   to open a selection dialog to view all available options.
 * - If all suggestions matching the query are already selected, a message is displayed to inform the user that there are no more options to select.
 * - If no suggestions match the query, a message is displayed to inform the user that there are no results available.
 */
@Component( {
    selector: 'his-multiselect-autocomplete',
    templateUrl: './his-multiselect-autocomplete.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
} )
export class HisMultiselectAutocompleteComponent {
    private readonly uiService = inject( HisUiService );
    private readonly backendService = inject( HisBackendService );
    private readonly notificationService = inject( HisNotificationService );

    readonly hisDdKey = input<string | undefined>();
    readonly inputId = input<string | undefined>();
    readonly dataSource = input<string | undefined>();
    readonly limit = input<number | null>( null );
    readonly field = input<string | null>( null );
    readonly group = input( false, { transform: booleanAttribute } );
    readonly disabled = input( false, { transform: booleanAttribute } );
    readonly showDropdownIcon = input( true, { transform: booleanAttribute } );
    readonly removeSelectedOptionsFromDropdown = input( false, { transform: booleanAttribute } );

    /** All available options from which the user may choose from. */
    @Input( { alias: 'optionList' } ) set optionListInput( value: HisOptionXtd[] | HisOptionGroup[] | null ) {
        this.optionList.set( value );
        if ( this.loading() && value != null ) this.loading.set( false );
    }
    /** Internal optionList signal. The public `optionList` input is defined as a setter to explicitly disable the loading state after receiving new options. */
    private readonly optionList = signal<HisOptionXtd[] | HisOptionGroup[] | null>( null );
    /** The options which were previously selected already. */
    readonly selectedOptions = input<HisOptionXtd[]>( [] );
    readonly selectedOptionsChange = output<HisOptionXtd[]>();
    /** Allows event binding to retrieve options loaded via the `dataSource`. */
    readonly dataSourceAvailable = output<HisOptionXtd[]>();
    /**
     * If the `optionList` is _null_ and no dataSource was defined, the first `complete` event from the p-autoComplete will
     * trigger this request to inform the parent it needs to fetch the suggestions now. This allows lazy loading on demand.
     */
    readonly lazyLoadRequest = output<void>();
    /** Request to open the selection dialog. Provides the current query input. */
    readonly openSelectionDialogRequest = output<string>();


    //* Suggestions pipeline: baseSuggestions -> filteredSuggestions -> unselectedSuggestions -> suggestions

    /** The final list of suggestions after filtering and limiting the length. */
    protected readonly suggestions = computed( () => this.limitSuggestions( this.unselectedSuggestions() ) );
    protected readonly isSuggestionsLimited = computed( () => !!this.limit() && this.unselectedSuggestions().length > this.limit() );
    /** The suggestions excluding the options that have already been selected. (if `removeSelectedOptionsFromDropdown` is true) */
    protected readonly unselectedSuggestions = computed( () => this.filterBySelection( this.filteredSuggestions() ) );
    /** The filtered suggestions to display after applying the query input. */
    private readonly filteredSuggestions = computed( () => this.filterByQuery( this.baseSuggestions(), this.queryPredicate() ) );
    protected readonly filteredSuggestionsCount = computed( () => this.filteredSuggestions().length ?? 0 );
    /** All available suggestions to display in the dropdown list. */
    private readonly baseSuggestions = computed( () => this.dataSourceResult()?.options ?? this.optionList() ?? [] );


    /** Options loaded via the `HisAutoCompleteEagerDataSourceDirective` using the `dataSource` input. */
    private readonly dataSourceResult = signal<DataSourceResult | null>( null );
    /** Fast lookup of selected option ids used for filtering. */
    private readonly selectedOptionIds = computed( () => new Set( this.selectedOptions().map( opt => String( opt.id ) ) ) );
    /** A filter predicate constructed with the last query input. */
    private readonly queryPredicate = signal<QueryPredicate>( this.buildQueryPredicate( '' ) );
    protected readonly loading = signal<boolean>( false );

    /** The active p-autoComplete component from the template. As there is always just one active depending on the inputs, one `viewChild` is enough. */
    private readonly autoComplete = viewChild<AutoComplete>( 'autoComplete' );

    /**
     * Handler for the `completeMethod` event of the p-autocomplete. Updates the filter query for suggestions.
     * 
     * If the the parent has a lazy load mechanism to fetch options, meaning the `optionList` is still null, the first call to this handler triggers the `lazyLoadRequest`
     * to inform the parent it should fetch the options now. The query is still applied immediately and will take effect as soon as the options arrive.
     */
    protected onSearch( event: AutoCompleteCompleteEvent ) {
        this.refreshSuggestions();
        const query = ( event.query ?? '' ).trim().toLowerCase();
        this.queryPredicate.set( this.buildQueryPredicate( query ) );
    }

    /**
     * Handler for the `onSelect` event of the p-autocomplete. Adds the selected option to the `selectedOptions` list and emits the change.
     * 
     * If the selected option is already in the `selectedOptions` list, a notification is shown and the option is not added again.
     * 
     * The input field is cleared after selection to allow for new queries.
     */
    protected onSelect( event: AutoCompleteSelectEvent ) {
        this.notificationService.clear();
        this.clearInput();

        const optionToSelect = event.value as HisOptionXtd;
        if ( !optionToSelect ) {
            return;
        }

        if ( optionToSelect.isDialogButton ) {
            this.openSelectionDialogRequest.emit( this.queryPredicate()?.query ?? '' );
            return;
        }

        if ( this.isOptionSelected( optionToSelect ) ) {
            const label = optionToSelect.label;
            const message = this.uiService.translate( 'genericSearch.alreadySelected', label );
            this.notificationService.info( message, 'Info' );
            return;
        }

        this.selectedOptionsChange.emit( [...this.selectedOptions(), optionToSelect] );
    }

    /** 
     * Clears the input on _blur_ of this component. Requires additional checks, as clicking the dropdown button also triggers the blur event,
     * even though the inputs focus is restored immediately, which should therefore not be cleared, as the component is still active.
     */
    protected blurHandler( event: Event ) {
        const { relatedTarget } = event as FocusEvent;
        if ( !relatedTarget || !(relatedTarget instanceof HTMLButtonElement) || !relatedTarget.classList.contains( 'p-autocomplete-dropdown' ) ) {
            this.clearInput();
        }
    }

    protected requestOptionDialog() {
        this.openSelectionDialogRequest.emit( this.queryPredicate()?.query ?? '' );
    }

    /**
     * Checks for missing or stale options and loads them on demand if necessary.\
     * If a `dataSource` is present, it takes priority.\
     * If the `optionList` is null and no `dataSource` is defined, a lazy load request is emitted to the parent.\
     * If a lazy load was already requested, it will not be requested again.
     */
    private refreshSuggestions() {
        if ( this.loading() ) {
            return;
        }

        if ( !!this.dataSource() && this.dataSourceResult()?.source !== this.dataSource() ) {
            // datasource is defined and was not yet loaded/source has changed
            console.debug( `requesting dataSource fetch for '${this.dataSource()}'` );
            this.loadFromDataSource( this.dataSource() );
            return;
        }

        if ( !this.optionList() ) {
            // no options available and no datasource defined, trigger lazy load request to parent
            console.debug( 'requesting lazy load from parent' );
            this.loading.set( true );
            this.lazyLoadRequest.emit();
        }
    }

    private clearInput() {
        this.autoComplete().clear();
    }

    private buildQueryPredicate( query: string ): QueryPredicate {
        const queryParts = query.trim().toLowerCase().split( ' ' );
        const predicate = ( opt: HisOptionXtd ) => queryParts.every( part => opt.label.toLowerCase().includes( part ) );
        predicate.query = query;
        return predicate;
    }

    private filterByQuery( optionsOrGroups: HisOptionXtd[] | HisOptionGroup[], predicate: Predicate<HisOptionXtd> ) {
        return this.filterSuggestions( optionsOrGroups, predicate );
    }

    private filterBySelection( optionsOrGroups: HisOptionXtd[] | HisOptionGroup[] ) {
        if ( !this.removeSelectedOptionsFromDropdown() || this.selectedOptionIds().size === 0 ) {
            return optionsOrGroups;
        }
        return this.filterSuggestions( optionsOrGroups, opt => !this.isOptionSelected( opt ) );
    }

    private filterSuggestions( optionsOrGroups: HisOptionXtd[] | HisOptionGroup[], predicate: Predicate<HisOptionXtd> ) {
        if ( optionsOrGroups == null || predicate == null ) {
            return [];
        }

        if ( this.isGroup( optionsOrGroups ) ) {
            return optionsOrGroups.map( group => {
                const { label, icon } = group;
                const items = this.doFilterSelectedOptions( group.items, predicate );
                return { items, label, icon } as HisOptionGroup;
            } ).filter( group => group.items.length > 0 );
        }

        return this.doFilterSelectedOptions( optionsOrGroups, predicate );
    }

    private doFilterSelectedOptions( options: HisOptionXtd[], predicate: Predicate<HisOptionXtd> ) {
        return options.filter( predicate );
    }

    private isOptionSelected( option: HisOptionXtd ) {
        return this.selectedOptionIds().has( String( option.id ) );
    }

    private isGroup( val: any ): val is HisOptionGroup[] {
        return this.group();
    }

    private hasUndefinedLabel( data: any[] ) {
        return data.some( val => val.label === undefined );
    }

    private defineLabel( data: any[] ) {
        return data.map( val => {
            const { id, label, ...rest } = val;
            const option = { ...rest, id, label: val[this.field()] ?? label ?? val.defaulttext };
            return option as HisOptionXtd;
        } );
    }

    private limitSuggestions( options: HisOptionXtd[] | HisOptionGroup[] ) {
        if ( !options ) {
            return null;
        }

        if ( !!this.limit() && this.limit() < options.length && !this.isGroup( options ) ) {
            return options.slice( 0, this.limit() );
        }
        
        return options;
    }

    /**
     * Loads options from the backend using the `dataSource` input. The `dataSource` may either be a url to a rest service or the class name of value entities.
     * 
     * To ensure the options are available after they were fetched, they are stored in the local `dataSourceOptions` property, which is used
     * as a fallback for `optionList`, so that implementing components are not required to rewire `dataSourceAvailable` into `optionList`.
     * 
     * @devnote **Explicitly avoids the usage of both hisDataSource directives, as they perform addtional programmatic mutation of the p-autoComplete component,
     * which leads to unexpected and conflicting behavior, distorting this component's implementation.**
     * 
     * TODO: move to dedicated service for centralized handling
     */
    private loadFromDataSource( source: string ) {
        this.loading.set( true );
        const url = source.includes( '/' ) ? source : 'api/v1/cs/sys/values/' + source;
        this.backendService.getData( url ).subscribe( data => {
            const options = this.hasUndefinedLabel( data ) ? this.defineLabel( data ) : [...data];
            this.dataSourceResult.set( { source, options } );
            this.dataSourceAvailable.emit( options );
            this.loading.set( false );
        } );
    }
}
